import React from 'react';
import { useActivity, ACTIVITY_STYLES } from '@/contexts/ActivityContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { X, Trash2, CheckCheck, Clock, History } from 'lucide-react';
import { cn } from '@/lib/utils';

// Formater le temps relatif
function formatRelativeTime(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 10) return "À l'instant";
  if (diffSec < 60) return `Il y a ${diffSec}s`;
  if (diffMin < 60) return `Il y a ${diffMin}min`;
  if (diffHour < 24) return `Il y a ${diffHour}h`;
  if (diffDay === 1) return 'Hier';
  if (diffDay < 7) return `Il y a ${diffDay}j`;
  
  return date.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Item d'activité individuel
function ActivityItem({ activity, onMarkAsRead }) {
  const style = ACTIVITY_STYLES[activity.type] || ACTIVITY_STYLES.update;
  
  return (
    <div 
      className={cn(
        "p-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer",
        !activity.read && "bg-blue-50/50 dark:bg-blue-900/10"
      )}
      onClick={() => onMarkAsRead(activity.id)}
    >
      <div className="flex items-start gap-3">
        {/* Icône */}
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0",
          style.bg, style.color
        )}>
          {style.icon}
        </div>
        
        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={cn(
              "font-medium text-sm truncate",
              !activity.read && "text-gray-900 dark:text-white",
              activity.read && "text-gray-600 dark:text-gray-400"
            )}>
              {activity.title}
            </p>
            {!activity.read && (
              <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" />
            )}
          </div>
          
          {activity.details && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {activity.details}
            </p>
          )}
          
          <div className="flex items-center gap-2 mt-1">
            {activity.module && (
              <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-500 dark:text-gray-400">
                {activity.module}
              </span>
            )}
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatRelativeTime(activity.timestamp)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Panneau principal
export function ActivityPanel() {
  const { 
    activities, 
    isPanelOpen, 
    closePanel, 
    markAsRead, 
    markAllAsRead, 
    clearHistory,
    unreadCount 
  } = useActivity();

  return (
    <Sheet open={isPanelOpen} onOpenChange={(open) => !open && closePanel()}>
      <SheetContent side="right" className="w-full sm:w-[400px] p-0">
        <SheetHeader className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-500" />
              <SheetTitle className="text-lg">Historique des actions</SheetTitle>
            </div>
            <Button variant="ghost" size="icon" onClick={closePanel}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <SheetDescription className="text-sm text-gray-500">
            {activities.length === 0 
              ? "Aucune action enregistrée"
              : `${activities.length} action${activities.length > 1 ? 's' : ''} • ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`
            }
          </SheetDescription>
          
          {/* Actions globales */}
          {activities.length > 0 && (
            <div className="flex gap-2 mt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
                className="text-xs"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Tout marquer lu
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearHistory}
                className="text-xs text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Effacer
              </Button>
            </div>
          )}
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-180px)]">
          {activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <History className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-sm">Aucune action récente</p>
              <p className="text-xs mt-1">Vos actions seront enregistrées ici</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {activities.map((activity) => (
                <ActivityItem 
                  key={activity.id} 
                  activity={activity} 
                  onMarkAsRead={markAsRead}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// Bouton flottant pour ouvrir le panneau
export function ActivityButton() {
  const { togglePanel, unreadCount } = useActivity();
  
  return (
    <Button
      variant="outline"
      size="icon"
      onClick={togglePanel}
      className="relative"
      title="Historique des actions"
    >
      <History className="w-4 h-4" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  );
}

export default ActivityPanel;
